import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Header,
  ContentLayout,
  Table,
  SpaceBetween,
  Box,
} from '@cloudscape-design/components';
import { Capability } from '@amzn/finspace-service-typescript-client';
import Button from '../common/Button';
import { TrajectoryLoader } from '../common/Loaders';
import { useCapabilitiesStore } from '../../store/capability-store';
import { ROUTES } from '../../constants';
import './capabilities-list-page.scss';

const CapabilitiesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { capabilities, isLoading, error, fetchCapabilities } = useCapabilitiesStore();

  // Fetch capabilities on component mount
  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  const handleEdit = useCallback((capabilityId: string) => {
    navigate(`${ROUTES.CAPABILITIES_EDIT}/${capabilityId}`);
  }, [navigate]);

  if (isLoading) {
    return (
      <ContentLayout defaultPadding>
        <div className="loading-state">
          <TrajectoryLoader size="140px" speed="2s" />
          <div className="loading-message">
            Loading capabilities...
          </div>
        </div>
      </ContentLayout>
    );
  }

  if (error) {
    return (
      <ContentLayout defaultPadding>
        <Box textAlign="center">
          Error loading capabilities: {error.message}
        </Box>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      defaultPadding
      header={<Header variant="h1">Capabilities</Header>}
    >
      <SpaceBetween direction="vertical" size="l">
        <Table
          columnDefinitions={[
            {
              id: 'capability',
              header: 'Name',
              cell: (item: Capability) => (
                <Box>
                  <div className="capability-name">
                    {item.name}
                  </div>
                </Box>
              ),
              width: "30%",
              minWidth: "200px",
            },
            {
              id: 'description',
              header: 'Description',
              cell: (item: Capability) => (
                <div className="capability-description">
                  {item.description || '-'}
                </div>
              ),
              width: "45%",
              minWidth: "300px",
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (item: Capability) => (
                <div className="capability-actions">
                  <Button
                    variant="secondary"
                    icon="edit"
                    onClick={() => handleEdit(item.id!)}
                  >
                    Edit
                  </Button>
                </div>
              ),
              width: "25%",
              minWidth: "150px",
            }
          ]}
          items={capabilities?.capabilities || []}
          loading={isLoading}
          loadingText="Loading capabilities..."
          empty={
            <Box textAlign="center">
              No capabilities found
            </Box>
          }
        />
      </SpaceBetween>
    </ContentLayout>
  );
};

export default React.memo(CapabilitiesListPage);
